import React from 'react';
import { styled, useResponsiveValue } from 'dripsy';
import { observer } from 'mobx-react-lite';

import { IconTouchable, IIconTouchableProps } from './IconTouchable';
import {
  Touchable,
  TouchableTypeEnum,
  ITouchableProps,
} from './Touchable';
import { TextTouchable } from './TextTouchable';
import { Loader } from './Loader';
import { passIf } from '@utils';

const TouchableContainer = styled(Touchable)({
  flexDirection: 'row',
  alignItems: 'center',
});

interface ITitleStyleProps {
  isResponsiveTitle: boolean;
}

const TitleTouchable = styled(TextTouchable)(
  (props: ITitleStyleProps) => ({
    color: '$heading800',
    variant: 'text.medium',
    ml: '$5',
    display: props.isResponsiveTitle ? ['none', 'flex'] : 'flex',
  }),
);

export interface IResponsiveIconTouchableProps
  extends Omit<IIconTouchableProps, 'style'>,
    Pick<ITouchableProps, 'style'> {
  title: string;
  isLoading?: boolean;
  isResponsiveTitle?: boolean;
  onPressTitle?: () => void;
}

export const ResponsiveIconTouchable =
  observer<IResponsiveIconTouchableProps>(
    ({
      onPress,
      onLongPress,
      onLayout,
      onPressTitle,
      hitSlop = 20,
      disabled,
      isLoading,
      title,
      isResponsiveTitle = true,
      style,
      ...props
    }) => {
      const disabledContainerTouchable = useResponsiveValue([
        true,
        false,
      ]);

      const isDisabledTouchable = disabled || isLoading;
      const isDisabledContainerTouchable =
        (isDisabledTouchable ||
          disabledContainerTouchable ||
          !!onPressTitle) &&
        isResponsiveTitle;
      const isDisabledIconTouchable =
        (isDisabledTouchable || !disabledContainerTouchable) &&
        !isResponsiveTitle;
      const isDisabledTitleTouchable = !onPressTitle;

      return (
        <TouchableContainer
          onPress={onPress}
          onLongPress={onLongPress}
          disabled={isDisabledContainerTouchable}
          hitSlop={hitSlop}
          onLayout={onLayout}
          style={style}
          type={passIf(!isResponsiveTitle, TouchableTypeEnum.Opacity)}
        >
          {isLoading ? (
            <Loader />
          ) : (
            <IconTouchable
              onPress={onPress}
              onLongPress={onLongPress}
              disabled={isDisabledIconTouchable}
              hitSlop={hitSlop}
              {...props}
            />
          )}

          <TitleTouchable
            onPress={onPressTitle}
            isResponsiveTitle={isResponsiveTitle}
            disabled={isDisabledTitleTouchable}
          >
            {title}
          </TitleTouchable>
        </TouchableContainer>
      );
    },
  );
